import { type StateCreator } from 'zustand';

import { type DetectTextRegion, type DetectTextResult } from '@air/feature-ag-ui-chat';

export const MAX_DETECT_TEXT_ENTRIES = 50;

export type DetectTextResultSlice = {
  byImageUrl: Record<string, DetectTextResult>;
  clearAll: () => void;
  clearDetectTextResult: (imageUrl: string) => void;
  clearOcrUiState: (imageUrl: string) => void;
  editedTextsByImageUrl: Record<string, Record<number, string>>;
  focusedRegionByImageUrl: Record<string, number | null>;
  getDetectTextResult: (imageUrl: string) => DetectTextResult | undefined;
  hoveredRegionByImageUrl: Record<string, number | null>;
  setDetectTextResult: (imageUrl: string, result: DetectTextResult) => void;
  setEditedText: (imageUrl: string, regionId: number, value: string) => void;
  setEditedTexts: (imageUrl: string, editedTexts: Record<number, string>) => void;
  setFocusedRegion: (imageUrl: string, index: number | null) => void;
  setHoveredRegion: (imageUrl: string, index: number | null) => void;
};

export const pruneEntries = (entries: Record<string, DetectTextResult>): Record<string, DetectTextResult> => {
  const keys = Object.keys(entries);

  if (keys.length <= MAX_DETECT_TEXT_ENTRIES) {
    return entries;
  }

  const kept = keys.slice(-MAX_DETECT_TEXT_ENTRIES);

  return Object.fromEntries(kept.map((key) => [key, entries[key]]));
};

const sortRegionsReadingOrder = (regions: DetectTextRegion[]): DetectTextRegion[] =>
  [...regions].sort((a, b) => a.boundingBox.y - b.boundingBox.y || a.boundingBox.x - b.boundingBox.x);

export type CreateDetectTextResultSlice = () => StateCreator<DetectTextResultSlice, [], [], DetectTextResultSlice>;

export const createDetectTextResultSlice: CreateDetectTextResultSlice = () => (set, get) => ({
  byImageUrl: {},

  clearAll: () =>
    set({
      byImageUrl: {},
      editedTextsByImageUrl: {},
      focusedRegionByImageUrl: {},
      hoveredRegionByImageUrl: {},
    }),

  clearDetectTextResult: (imageUrl) =>
    set((state) => {
      const { [imageUrl]: _result, ...restResults } = state.byImageUrl;
      const { [imageUrl]: _edits, ...restEdits } = state.editedTextsByImageUrl;
      const { [imageUrl]: _focused, ...restFocused } = state.focusedRegionByImageUrl;
      const { [imageUrl]: _hovered, ...restHovered } = state.hoveredRegionByImageUrl;

      return {
        byImageUrl: restResults,
        editedTextsByImageUrl: restEdits,
        focusedRegionByImageUrl: restFocused,
        hoveredRegionByImageUrl: restHovered,
      };
    }),

  clearOcrUiState: (imageUrl) =>
    set((state) => {
      const { [imageUrl]: _edits, ...restEdits } = state.editedTextsByImageUrl;
      const { [imageUrl]: _focused, ...restFocused } = state.focusedRegionByImageUrl;
      const { [imageUrl]: _hovered, ...restHovered } = state.hoveredRegionByImageUrl;

      return {
        editedTextsByImageUrl: restEdits,
        focusedRegionByImageUrl: restFocused,
        hoveredRegionByImageUrl: restHovered,
      };
    }),
  editedTextsByImageUrl: {},
  focusedRegionByImageUrl: {},

  getDetectTextResult: (imageUrl) => get().byImageUrl[imageUrl],
  hoveredRegionByImageUrl: {},

  setDetectTextResult: (imageUrl, result) =>
    set((state) => ({
      byImageUrl: pruneEntries({
        ...state.byImageUrl,
        [imageUrl]: { ...result, regions: sortRegionsReadingOrder(result.regions) },
      }),
    })),

  setEditedText: (imageUrl, regionId, value) =>
    set((state) => ({
      editedTextsByImageUrl: {
        ...state.editedTextsByImageUrl,
        [imageUrl]: {
          ...(state.editedTextsByImageUrl[imageUrl] ?? {}),
          [regionId]: value,
        },
      },
    })),

  setEditedTexts: (imageUrl, editedTexts) =>
    set((state) => ({
      editedTextsByImageUrl: {
        ...state.editedTextsByImageUrl,
        [imageUrl]: editedTexts,
      },
    })),

  setFocusedRegion: (imageUrl, index) =>
    set((state) => ({
      focusedRegionByImageUrl: {
        ...state.focusedRegionByImageUrl,
        [imageUrl]: index,
      },
    })),

  setHoveredRegion: (imageUrl, index) =>
    set((state) => ({
      hoveredRegionByImageUrl: {
        ...state.hoveredRegionByImageUrl,
        [imageUrl]: index,
      },
    })),
});
